import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Table } from 'antd';
import * as Io5Icons from 'react-icons/io5';
import { NODE_BACKEND_API_URL } from '../../config';
import './variabletable.css';

const LOW_MARGIN_THRESHOLD = 5;
const MEDIUM_MARGIN_THRESHOLD = 10;

function computeMarginPct(discountFactor, markupFactor) {
    const d = Number(discountFactor);
    const m = Number(markupFactor);
    if (!isFinite(d) || !isFinite(m) || d <= 0 || m < 1) return null;
    return (1 - 1 / (d * m)) * 100;
}

function buildMarginTooltip(record) {
    const d = Number(record.DiscountFactor);
    const m = Number(record.MarkupFactor);
    const margin = computeMarginPct(d, m);
    if (margin === null) {
        return 'Valores de DESC. o GAN. no permiten calcular un margen (DESC debe ser > 0 y GAN >= 1).';
    }
    const formatted = margin.toFixed(2);
    const formula = `Margen esperado = (1 - 1 / (DESC x GAN)) x 100`;
    const calc = `= (1 - 1 / (${d} x ${m})) x 100 = ${formatted}%`;
    const level = margin < LOW_MARGIN_THRESHOLD
        ? `Esta linea esta por DEBAJO del ${LOW_MARGIN_THRESHOLD}% (umbral critico).`
        : `Esta linea esta por DEBAJO del ${MEDIUM_MARGIN_THRESHOLD}% (umbral de atencion).`;
    return `${formula}\n${calc}\nPrecio de venta = Costo x DESC x GAN.\n${level}\nAjusta DESC. y/o GAN. para alcanzar al menos el ${LOW_MARGIN_THRESHOLD}%.`;
}

function VariableTable({ lastUpdatedBrand }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [flashingBrand, setFlashingBrand] = useState(null);
    const flashTimeoutRef = useRef(null);

    // Helper function to fetch data from the API
    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${NODE_BACKEND_API_URL}/getbrandvariables/`);
            // Transform data to include key for ANT Design Table
            const transformedData = res.data
                .map(item => ({
                    ...item,
                    key: item.BrandName, // ANT Design Table requires unique key
                }));
            setData(transformedData);
        } catch (er) {
            console.error("Error fetching variable data:", er);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    // Effect hook to fetch data on component mount and when lastUpdatedBrand changes
    useEffect(() => {
        fetchData();
    }, [lastUpdatedBrand]);

    // Effect hook for handling the flashing animation
    useEffect(() => {
        if (lastUpdatedBrand) {
            setFlashingBrand(lastUpdatedBrand);

            // Clear any existing timeout
            if (flashTimeoutRef.current) {
                clearTimeout(flashTimeoutRef.current);
            }

            // Set a new timeout to remove the flashing class after 3 seconds
            flashTimeoutRef.current = setTimeout(() => {
                setFlashingBrand(null);
                flashTimeoutRef.current = null;
            }, 3000);

            // Cleanup function
            return () => {
                if (flashTimeoutRef.current) {
                    clearTimeout(flashTimeoutRef.current);
                }
            };
        }
    }, [lastUpdatedBrand]);

    // Helper function to format the date
    const formatDate = (dateString) => {
        if (!dateString) return '';

        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }

        const day = date.getUTCDate().toString().padStart(2, '0');
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
        const year = date.getUTCFullYear();

        return `${day}/${month}/${year}`;
    };

    // Define ANT Design Table columns
    const columns = [
        {
            title: 'MARCA',
            dataIndex: 'BrandName',
            key: 'BrandName',
            sorter: (a, b) => a.BrandName.localeCompare(b.BrandName),
            defaultSortOrder: 'ascend',
            width: '25%',
        },
        {
            title: 'IVA',
            dataIndex: 'TaxFactor',
            key: 'TaxFactor',
            width: '15%',
            align: 'center',
        },
        {
            title: 'DESC.',
            dataIndex: 'DiscountFactor',
            key: 'DiscountFactor',
            width: '15%',
            align: 'center',
        },
        {
            title: 'GAN.',
            dataIndex: 'MarkupFactor',
            key: 'MarkupFactor',
            width: '15%',
            align: 'center',
        },
        {
            title: 'MODIFICACION',
            dataIndex: 'LastChangeDate',
            key: 'LastChangeDate',
            sorter: (a, b) => new Date(a.LastChangeDate) - new Date(b.LastChangeDate),
            render: (date) => formatDate(date),
            width: '30%',
            align: 'center',
        },
    ];

    return (
        <div id='divenv'>
            <Io5Icons.IoBarChartSharp size={42} />
            <h2>VARIABLES ACTUALES</h2>
            <div className="variable-table-div">
                <Table
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    pagination={false}
                    size="small"
                    locale={{
                        emptyText: 'No hay datos de variables disponibles.'
                    }}
                    rowClassName={(record) => {
                        if (record.BrandName === flashingBrand) return 'ant-table-row-flashing';
                        if (Number(record.MarkupFactor) === 1.00) return '';
                        const margin = computeMarginPct(record.DiscountFactor, record.MarkupFactor);
                        if (margin === null) return '';
                        if (margin < LOW_MARGIN_THRESHOLD) return 'ant-table-row-low-margin';
                        if (margin < MEDIUM_MARGIN_THRESHOLD) return 'ant-table-row-medium-margin';
                        return '';
                    }}
                    onRow={(record) => {
                        if (Number(record.MarkupFactor) === 1.00) {
                            return { title: 'Marca con margenes pre-acordados. El margen se define en la ficha de cada producto (Prices vs ProductCost), no por Variables.' };
                        }
                        const margin = computeMarginPct(record.DiscountFactor, record.MarkupFactor);
                        if (margin === null || margin >= MEDIUM_MARGIN_THRESHOLD) return {};
                        return { title: buildMarginTooltip(record) };
                    }}
                />
            </div>
        </div>
    );
}

export default VariableTable;